import {
  FC,
  ReactNode,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import {
  Link,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { useSearchRevenueSchedules } from '../../api/revenueService';
import {
  MCustomLinkTab,
  MFlex,
  MLink,
  MPageContainer,
  MPageHeader,
  MPageLoader,
  MTabList,
  MTabs,
} from '../../components/Monetize';
import { ROUTES } from '../../constants';
import {
  getQuoteEditRoute,
  getSalesContractEditRoute,
} from '../../constants/routes';
import { useDocumentHead } from '../../services/documentHead';
import { ITabRoute } from '../../types';
import { RevenueContractScheduleTab } from './RevenueContractTabs/RevenueContractScheduleTab';
import { RevenueContractSummaryTab } from './RevenueContractTabs/RevenueContractSummaryTab';

// Revenue Journal and Quotes tabs will return once their backend support is in
// place.
const REVENUE_CONTRACT_TAB_ROUTES: ITabRoute[] = [
  {
    title: 'Summary',
    route: ROUTES.REVENUE_CONTRACT_SUMMARY,
    id: 'summary',
  },
  {
    title: 'Revenue Schedule',
    route: ROUTES.REVENUE_CONTRACT_SCHEDULE,
    id: 'revenue-schedule',
  },
];

export const RevenueContractPage: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { revenueContractId = '' } = useParams();
  const { setDocTitle } = useDocumentHead();

  const [activeTabIndex, setActiveTabIndex] = useState<number>(0);

  const { data: schedulesData } = useSearchRevenueSchedules(
    { revenueContractId },
    { enabled: !!revenueContractId, refetchOnWindowFocus: false },
  );

  const quoteId = useMemo(
    () =>
      (schedulesData?.content ?? []).find(
        (schedule) => !!schedule.references?.quoteId,
      )?.references?.quoteId,
    [schedulesData],
  );

  const contractId = useMemo(
    () =>
      (schedulesData?.content ?? []).find(
        (schedule) => !!schedule.references?.contractId,
      )?.references?.contractId,
    [schedulesData],
  );

  const extraSubtitleParts = useMemo<ReactNode[]>(() => {
    const parts: ReactNode[] = [];
    if (quoteId) {
      parts.push(
        <MLink
          key="view-quote"
          asChild
          fontSize="sm"
          fontWeight="400"
          _hover={{ textDecoration: 'underline' }}
        >
          <Link to={getQuoteEditRoute(quoteId)}>View Quote</Link>
        </MLink>,
      );
    }
    if (contractId) {
      parts.push(
        <MLink
          key="view-contract"
          asChild
          fontSize="sm"
          fontWeight="400"
          _hover={{ textDecoration: 'underline' }}
        >
          <Link to={getSalesContractEditRoute(contractId)}>View Contract</Link>
        </MLink>,
      );
    }
    return parts;
  }, [contractId, quoteId]);

  useEffect(() => {
    setDocTitle(
      revenueContractId
        ? `Revenue Contract - ${revenueContractId}`
        : 'Revenue Contract',
    );
  }, [revenueContractId, setDocTitle]);

  useEffect(() => {
    if (!revenueContractId) {
      navigate(ROUTES.REVENUE, { replace: true });
    }
  }, [revenueContractId, navigate]);

  useLayoutEffect(() => {
    const index = REVENUE_CONTRACT_TAB_ROUTES.findIndex(({ route }) =>
      location.pathname.includes(route),
    );
    setActiveTabIndex(index < 0 ? 0 : index);
  }, [location.pathname]);

  if (!revenueContractId) {
    return <MPageLoader />;
  }

  return (
    <MPageContainer alignItems="stretch" enableAccountSearch>
      <MPageHeader
        hasBackButton
        title="Revenue Contract"
        id={revenueContractId}
        copyUrl
        extraSubtitleParts={extraSubtitleParts}
      />
      <MFlex flex="1" ml={8} minH="0" flexDirection="column">
        <MTabs
          data-testid="revenue-contract-tabs"
          size="sm"
          index={activeTabIndex}
          // `MCustomLinkTab`s navigate themselves — see its doc comment.
          navigate={null}
        >
          <MTabList alignItems="flex-end">
            {REVENUE_CONTRACT_TAB_ROUTES.map(({ route, title }) => (
              <MCustomLinkTab to={route} key={route}>
                {title}
              </MCustomLinkTab>
            ))}
          </MTabList>
        </MTabs>

        <Routes>
          <Route
            path="/"
            element={
              <RevenueContractSummaryTab
                revenueContractId={revenueContractId}
              />
            }
          />
          <Route
            path={ROUTES.REVENUE_CONTRACT_SUMMARY}
            element={
              <RevenueContractSummaryTab
                revenueContractId={revenueContractId}
              />
            }
          />
          <Route
            path={ROUTES.REVENUE_CONTRACT_SCHEDULE}
            element={
              <RevenueContractScheduleTab
                revenueContractId={revenueContractId}
              />
            }
          />
        </Routes>
      </MFlex>
    </MPageContainer>
  );
};
